<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import {
    computed,
    inject,
    onMounted,
    provide,
    ref,
    watchEffect,
    type ComputedRef,
    type Ref,
  } from 'vue';
  import { useRoute, type RouteLocationRaw } from 'vue-router';
  import { useDisplay, useTheme } from 'vuetify';

  import { useMainPageRoute } from '@/composables/useMainPageRoute.composable';
  import { EnumRoutes } from '@/helpers/enums/routes.enum';
  import { useI18 } from '@/plugins';
  import { useAuthStore } from '@/stores/auth.store';
  import { useLocaleStore } from '@/stores/locale.store';
  import { useOrganizationStore } from '@/stores/organzation.pinia';
  import { usePermissionsStore } from '@/stores/permissoins.store';
  import ThreadIcon from '@/submodules/himam-common-components/src/components/threads/ThreadIcon.vue';
  import type { headerItem } from '@/submodules/himam-common-components/src/helpers/interfaces/header-item.interface';

  import ToggleTheme from '@/components/ToggleTheme.vue';

  import CartIcon from '../CartIcon.vue';
  import FavoriteIcon from '../FavoriteIcon.vue';
  import LanguageToggle from '../LanguageToggle.vue';
  import LoginAndRegister from '../LoginAndRegister.vue';
  import NotificationIcon from '../notification/NotificationIcon.vue';
  import ProfileIcon from '../ProfileIcon.vue';

  import MenuDrawer from './drawer/MenuDrawer.vue';
  import SearchField from './HeaderSearchField.vue';

  const organizationStore = useOrganizationStore();
  const { organization } = storeToRefs(organizationStore);
  const path = computed(() => {
    return route.path == '/' ? window.location.pathname : route.path;
  });

  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);

  const emit = defineEmits<{
    (event: 'update', value: boolean): void;
  }>();

  const route = useRoute();
  const isLoggedIn = computed(() => !!user.value?.id);
  const showCartIcon = computed(() =>
    checkAuthentication(EnumRoutes.financials.cartPage.meta)
  );
  const showOnlyLogo = computed(
    () => path.value.startsWith('/assessments') ?? route.meta?.showOnlyLogo
  );

  const homePage = useMainPageRoute;
  const openMenu = ref(false);
  const appBar = ref(false);
  const activeItem: Ref<RouteLocationRaw[]> = ref([]);
  const headerItems: ComputedRef<headerItem[]> | undefined =
    inject('headerItems');
  const activeRouteName = computed(() => route.meta.activeRouteName);
  watchEffect(() => {
    let firstMath = '';
    headerItems?.value.forEach((item) => {
      if (!item.children) {
        if (item.routeName === activeRouteName.value) {
          firstMath = item.to as string;
          activeItem.value = [item.to as string];
          return;
        }
        if (
          path.value.startsWith(item.to as string) &&
          (item.to as string)?.length >= firstMath.length
        ) {
          firstMath = item.to as string;
          activeItem.value = [item.to as string];
        }
      } else {
        item.children.forEach((child) => {
          if (item.routeName === activeRouteName.value) {
            firstMath = item.to as string;
            activeItem.value = [item.to as string];
            return;
          }
          if (
            path.value.startsWith(child.to as string) &&
            (child.to as string)?.length >= firstMath.length
          ) {
            firstMath = child.to as string;
            activeItem.value = [child.to as string];
          }
        });
      }
    });
    if (!firstMath.length) activeItem.value = [];
  });
  const isAuthPage = computed(() => {
    return (
      path.value.startsWith('/auth') ||
      path.value.startsWith('/register') ||
      !user.value
    );
  });

  const EA_LANDING_URL = 'https://expertsacademy.sa';
  const useEALandingLogoLink = computed(() => organization.value?.id === 7);

  const { mdAndUp, lgAndUp } = useDisplay();
  const { t } = useI18();
  const localeStore = useLocaleStore();
  const { isLTR } = storeToRefs(localeStore);
  const theme = useTheme();
  onMounted(() => {
    const current = localStorage.getItem('theme');
    theme.global.name.value =
      current === 'myCustomDarkTheme'
        ? 'myCustomDarkTheme'
        : 'myCustomLightTheme';
  });

  const currentTheme = computed(() => theme.global.name.value);

  const logo = computed(() =>
    currentTheme.value === 'myCustomDarkTheme'
      ? (organization.value?.darkLogo?.url ?? organization.value?.logo?.url)
      : organization.value?.logo?.url
  );
  const mobileLogo = computed(() =>
    currentTheme.value === 'myCustomDarkTheme'
      ? (organization.value?.darkMobileLogo?.url ??
        organization.value?.mobileLogo?.url)
      : organization.value?.mobileLogo?.url
  );
  provide('conversationIconColor', ref('#4E4E4E'));
  const permissionsStore = usePermissionsStore();
  const { checkAuthentication } = permissionsStore;
</script>

<template>
  <v-app-bar
    v-if="showOnlyLogo"
    elevation="0"
    height="60"
    class="pa-4 px-sm-5 py-md-8 flex-grow-0 justify-center bg-transparent"
    :absolute="true"
    @update:model-value="emit('update', appBar)"
  >
    <div class="flex flex-column ga-1 mx-auto">
      <a v-if="useEALandingLogoLink" :href="EA_LANDING_URL" class="d-block">
        <img
          :src="logo"
          alt="Expert Logo"
          contain
          height="60px"
          :width="undefined"
          class="mx-auto d-block"
        />
      </a>
      <img
        v-else
        :src="logo"
        alt="Expert Logo"
        contain
        height="60px"
        :width="undefined"
        class="mx-auto d-block"
      />
      <a
        v-if="organization?.parentOrganization"
        target="_blank"
        :href="`https://${organization.parentOrganization.urls?.find((u) => u.isMain)?.studentUrl}`"
        class="mx-auto mx-md-0"
      >
        <div
          class="text-caption text-outline"
          v-html="
            t('shared.poweredBy', {
              organization: isLTR
                ? organization.parentOrganization.name
                : (organization.parentOrganization.nameArabic ??
                  organization.parentOrganization.name),
            })
          "
        ></div>
      </a>
    </div>
  </v-app-bar>
  <MenuDrawer
    id="menu-drawer-items-opening"
    :open-menu="openMenu"
    @update:open-menu="(val) => (openMenu = val)"
  >
  </MenuDrawer>
  <v-app-bar
    v-if="!showOnlyLogo"
    v-model="appBar"
    :absolute="false"
    scroll-behavior="hide"
    :scroll-threshold="243"
    :height="mdAndUp ? '104' : '72'"
    elevation="0"
    :class="lgAndUp ? 'app-bar-header' : ''"
    class="px-4 px-sm-5"
    style="border-bottom: 0.5px solid #e0e0e0"
    @update:model-value="emit('update', appBar)"
  >
    <template v-if="!mdAndUp" #prepend>
      <v-app-bar-nav-icon
        aria-label="open or close menu"
        density="compact"
        class="d-block d-md-none"
        @click.prevent="openMenu = !openMenu"
      ></v-app-bar-nav-icon>
    </template>
    <v-app-bar-title class="ma-0">
      <div class="d-flex align-center ga-4 ga-md -8">
        <div class="flex flex-column ga-1">
          <a
            v-if="useEALandingLogoLink"
            :href="EA_LANDING_URL"
            class="mx-auto mx-md-0"
          >
            <img
              :src="logo"
              alt="Expert Logo"
              contain
              height="35px"
              :width="undefined"
              class="mx-auto d-none d-md-block"
            />
            <img
              :src="mobileLogo ?? logo"
              alt="Expert Logo"
              contain
              height="35px"
              :width="undefined"
              class="mx-auto d-block d-md-none"
            />
          </a>
          <RouterLink
            v-else
            :to="{ name: homePage.name }"
            class="mx-auto mx-md-0"
          >
            <img
              :src="logo"
              alt="Expert Logo"
              contain
              height="35px"
              :width="undefined"
              class="mx-auto d-none d-md-block"
            />
            <img
              :src="mobileLogo ?? logo"
              alt="Expert Logo"
              contain
              height="35px"
              :width="undefined"
              class="mx-auto d-block d-md-none"
            />
          </RouterLink>
          <a
            v-if="organization?.parentOrganization"
            target="_blank"
            :href="`https://${organization.parentOrganization.urls?.find((u) => u.isMain)?.studentUrl}`"
            class="mx-auto mx-md-0"
          >
            <div
              class="text-caption text-outline"
              v-html="
                t('shared.poweredBy', {
                  organization: isLTR
                    ? organization.parentOrganization.name
                    : (organization.parentOrganization.nameArabic ??
                      organization.parentOrganization.name),
                })
              "
            ></div>
          </a>
        </div>
        <v-tabs class="align-center d-none d-md-block" hide-slider>
          <v-tab
            v-for="(item, i) in headerItems"
            :key="item.title"
            :to="item.to"
            class="pa-1 app-bar-tab"
            min-width="0"
            :class="[
              i != (headerItems?.length || 0) - 1 ? 'me-2 me-sm-4 me-lg-8' : '',
            ]"
          >
            <span
              :class="[
                activeItem?.[0] === item.to
                  ? 'text-black active-tab'
                  : 'text-gray',
              ]"
              class="position-relative"
            >
              {{ item.title }}
            </span>
          </v-tab>
        </v-tabs>
        <SearchField
          v-if="!isAuthPage && mdAndUp && user?.isVerified !== false"
          :app-bar="appBar"
        ></SearchField>
      </div>
    </v-app-bar-title>
    <template #append>
      <div class="d-flex">
        <!--if user Authorized  -->
        <div v-if="isLoggedIn" class="d-flex ga-3 align-center">
          <FavoriteIcon v-if="mdAndUp" />
          <CartIcon v-if="showCartIcon" />
          <v-divider v-if="mdAndUp" vertical color="primary"></v-divider>
          <div class="d-flex align-start ga-3">
            <NotificationIcon />
            <ThreadIcon />
          </div>
          <ProfileIcon />
        </div>
        <div v-else class="ga-2 d-none d-md-flex">
          <ToggleTheme :show-as-menu="false" />
          <LanguageToggle />
          <LoginAndRegister />
        </div>
      </div>
    </template>
  </v-app-bar>
</template>
<style lang="scss">
  .app-bar-header {
    padding-inline: 108px !important;
  }
  .active-tab {
    border-bottom: 3px $secondary solid;
  }
  .beta-chip {
    top: 0;
    right: 0;
    max-width: none;
    transform: translate(50%, -100%);
  }
  .ar-beta-chip {
    top: 0;
    left: 0;
    max-width: none;
    transform: translate(-50%, -100%);
  }
  .app-bar-tab .v-btn__overlay {
    height: 40px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
  }
</style>
